/**
 * Projects horizontal scroll-pin with lerp smoothing.
 * Section pinned (sticky); vertical scroll translates the grid horizontally.
 * Lerp interpolates current → target each frame for buttery motion at extremes.
 */
export default function initProjectsScroll() {
    const section = document.querySelector('.section-projects');
    if (!section) return;
    const wrapper = section.querySelector('.projects-list-wrapper');
    const list = section.querySelector('.projects-list');
    if (!wrapper || !list) return;

    const sticky = section.querySelector('.projects__content');
    // Mobile, or short tablet-band landscape (e.g. iPhone Duo unfolded 890×626):
    // the pinned grid is taller than the viewport there, so fall back to stacked cards.
    // Keep in sync with the matching media queries in index.css.
    const mql = window.matchMedia('(max-width: 809px), (min-width: 810px) and (max-width: 1199.98px) and (max-height: 700px)');

    let scrollDistance = 0;
    let stickyHeight = 0;
    let leadIn = 0;
    let targetX = 0;
    let currentX = 0;
    const lerpFactor = 0.12;
    let rafActive = false;

    function setup() {
        if (mql.matches) {
            section.style.height = '';
            list.style.transform = '';
            if (sticky) sticky.style.top = '';
            currentX = 0;
            targetX = 0;
            return;
        }
        const listWidth = list.scrollWidth;
        const wrapperWidth = wrapper.clientWidth;
        scrollDistance = Math.max(0, listWidth - wrapperWidth);
        wrapper.classList.toggle('projects-list-wrapper--centered', scrollDistance === 0);
        stickyHeight = sticky ? sticky.offsetHeight : window.innerHeight;
        // Delay so horizontal scroll starts when projects-list midline reaches viewport midline.
        // Sticky pins at top:0; list-mid in viewport = listMidInSticky (constant once pinned).
        // Need section scroll past pin point by (listMidInSticky - viewportMid) for visual alignment,
        // plus extra buffer so user perceives the centered state before motion starts.
        const listRect = list.getBoundingClientRect();
        const stickyRect = sticky.getBoundingClientRect();
        const listMidInSticky = (listRect.top - stickyRect.top) + listRect.height / 2;
        const viewportMid = window.innerHeight / 2;
        // Pin sticky at the offset that places list-mid at viewport-mid
        const stickyTop = viewportMid - listMidInSticky;
        sticky.style.top = `${stickyTop}px`;
        const dwell = window.innerHeight * 0.5;
        leadIn = dwell;
        section.style.height = `${stickyHeight + scrollDistance + leadIn}px`;
        updateTarget();
        if (!rafActive) tick();
    }

    function updateTarget() {
        if (mql.matches || scrollDistance === 0) {
            targetX = 0;
            return;
        }
        const rect = section.getBoundingClientRect();
        const scrolled = -rect.top;
        const adjustedScroll = Math.max(0, scrolled - leadIn);
        const total = section.offsetHeight - stickyHeight - leadIn;
        const rawProgress = total > 0 ? adjustedScroll / total : 0;
        const progress = Math.max(0, Math.min(1, rawProgress));
        targetX = -progress * scrollDistance;
    }

    function tick() {
        rafActive = true;
        const diff = targetX - currentX;
        if (Math.abs(diff) < 0.05) {
            currentX = targetX;
            list.style.transform = `translate3d(${currentX}px, 0, 0)`;
            rafActive = false;
            return;
        }
        currentX += diff * lerpFactor;
        list.style.transform = `translate3d(${currentX}px, 0, 0)`;
        requestAnimationFrame(tick);
    }

    function onScroll() {
        updateTarget();
        if (!rafActive) tick();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', setup);
    mql.addEventListener('change', setup);

    if (document.readyState === 'complete') {
        setup();
    } else {
        window.addEventListener('load', setup);
    }
}
